import { useEffect, useState } from "react";
import { LOADING } from "@/lib/uiMessages";

type WinWithBoot = Window & {
  __lovBootHidden?: boolean;
};

/**
 * Fallback dla React.Suspense — pokazywany gdy lazy-loaded route ładuje swój chunk JS.
 *
 * Na pierwszym wejściu na stronę bootloader z `index.html` (#boot-fallback) jest jeszcze
 * widoczny pod spodem, więc nie renderujemy własnego spinnera (uniknięcie efektu
 * "podwójnej ikonki ładowania"). Przy nawigacji między stronami bootloader jest już
 * usunięty (BootCleanup), więc pokazujemy nasz spinner normalnie.
 */
export const RouteFallback = () => {
  const [showOwnSpinner, setShowOwnSpinner] = useState(() => {
    if (typeof window === "undefined") return true;
    if ((window as unknown as WinWithBoot).__lovBootHidden) return true;
    // Brak boot-fallbacka w DOM (np. testy jsdom, środowiska bez index.html bootloadera) – pokaż spinner.
    if (typeof document !== "undefined" && !document.getElementById("boot-fallback")) return true;
    return false;
  });

  useEffect(() => {
    if (showOwnSpinner) return;
    // Jeśli bootloader nie zniknął w ciągu 100ms (nawigacja między trasami albo nieoczekiwany stan),
    // pokaż własny spinner zamiast pustego ekranu.
    const t = setTimeout(() => setShowOwnSpinner(true), 100);
    return () => clearTimeout(t);
  }, [showOwnSpinner]);

  if (!showOwnSpinner) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="min-h-screen bg-background flex flex-col items-center justify-center gap-3 p-4"
    >
      <div
        className="h-9 w-9 rounded-full border-2 border-primary/30 border-t-primary animate-spin"
        aria-hidden="true"
      />
      <p className="text-sm text-muted-foreground font-medium">{LOADING}</p>
    </div>
  );
};
